import type { LearnFAQ } from "@/pages/learn/learnShared";
import {
  makeEsArticle,
  esSaintRelated,
  type EsArticleInput,
} from "./learnFactoryEs";

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  published: "November 3, 2026",
  lastReviewed: "November 3, 2026",
  lastUpdated: "November 3, 2026",
};

const faqs: LearnFAQ[] = [
  {
    question: "¿Qué preguntar a un proveedor de SAINT®?",
    answer:
      "Pregunte si sigue el protocolo autorizado por la FDA (K220177), si usa orientación por fMRI, cómo evalúa la idoneidad y qué resultados esperan. Los resultados individuales varían.",
  },
  {
    question: "¿SAINT® tiene garantía de resultados?",
    answer:
      "No. Ningún tratamiento para la depresión ofrece garantía de resultados. Los resultados individuales varían. SAINT® está autorizado por la FDA (K220177) para adultos cuya depresión no ha mejorado con antidepresivos.",
  },
];

export const esQuestionsToAskProvider = makeEsArticle({
  slug: "questions-to-ask-a-saint-provider",
  title: "Preguntas para hacer a un proveedor de SAINT® | Neuro Wellness",
  description:
    "Preguntas clave para hacer a un proveedor de SAINT®: protocolo autorizado por la FDA, orientación por fMRI, evaluación de idoneidad y resultados esperados.",
  h1: "Preguntas para hacer a un proveedor de SAINT®",
  breadcrumbLabel: "Preguntas para un proveedor de SAINT®",
  shortAnswer:
    "Al evaluar un proveedor de SAINT®, pregunte si sigue el protocolo autorizado por la FDA (K220177), si usa orientación por fMRI, cómo evalúa la idoneidad y qué resultados esperan. Los resultados individuales varían; ningún tratamiento ofrece garantía. SAINT® está autorizado por la FDA para adultos cuya depresión no ha mejorado con antidepresivos.",
  keyTakeaways: [
    "Pregunte si sigue el protocolo autorizado por la FDA (K220177).",
    "Pregunte si usa orientación por fMRI.",
    "Pregunte cómo evalúa la idoneidad.",
    "Los resultados individuales varían.",
    "Ningún tratamiento ofrece garantía.",
  ],
  sections: [
    {
      heading: "Preguntas clave",
      paragraphs: ["Pregunte a un proveedor de SAINT®:"],
      list: [
        "¿Sigue el protocolo autorizado por la FDA (K220177)?",
        "¿Usa orientación por fMRI?",
        "¿Cómo evalúa la idoneidad?",
        "¿Qué resultados esperan y cómo los miden?",
        "¿Qué apoyo hay durante y después del tratamiento?",
      ],
    },
    {
      heading: "Resultados",
      paragraphs: [
        "Los resultados individuales varían. Ningún tratamiento para la depresión ofrece garantía. SAINT® está autorizado por la FDA para adultos con trastorno depresivo mayor que no han mejorado con antidepresivos. [[Solicite una consulta|/es/saint]] o llame al 954-827-9793.",
      ],
    },
  ],
  faqs,
  sources: [],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);

export const esWhatIsVerifiedProvider = makeEsArticle({
  slug: "what-is-a-verified-saint-provider",
  title: `¿Es realmente SAINT®? Cómo verificar una clínica de SAINT® TMS (2026)`,
  description: `Antes de pagar por SAINT®, verifique la clínica: búsquela en el localizador de Magnus Medical, confirme que la orientación está autorizada por la FDA y pregunte si el protocolo estuvo en los ensayos de SAINT.`,
  h1: `¿Es realmente SAINT®? Cómo saberlo antes de pagar`,
  breadcrumbLabel: "Verificar un proveedor de SAINT®",
  shortAnswer: `SAINT® se ofrece únicamente en el Sistema de Neuromodulación Magnus autorizado por la FDA con orientación guiada por fMRI, y solo en clínicas que figuran en el localizador oficial de Magnus Medical. Antes de pagar, confirme tres cosas: (1) la clínica está en el localizador de Magnus Medical, (2) el software de orientación está autorizado por la FDA (510(k) K220177) y (3) el protocolo se probó en los ensayos clínicos de SAINT. Neuro Wellness TMS Centers of America en Coral Springs, Florida, es un proveedor verificado de SAINT®. Si está en crisis, llame o envíe un mensaje de texto al 988.`,
  keyTakeaways: [
    `Verifique una clínica de SAINT® en el localizador oficial de Magnus Medical (magnusmedical.com/find-a-provider).`,
    `Confirme que el software de orientación está autorizado por la FDA (510(k) K220177).`,
    `Pregunte si el protocolo exacto se probó en los ensayos clínicos de SAINT.`,
    `Una clínica que no figura en el localizador ofrece un calendario acelerado off-label, no SAINT®.`,
    `Neuro Wellness en Coral Springs, FL, es un proveedor verificado de SAINT®.`,
  ],
  sections: [
    {
      heading: "Una verificación de 3 pasos antes de pagar",
      paragraphs: [
        `Antes de comprometerse con cualquier clínica que anuncie SAINT®, haga esta verificación de 3 pasos. Toma unos minutos y lo protege de pagar por un tratamiento distinto vendido bajo el nombre de SAINT.`,
      ],
      list: [
        `1. Busque la clínica en el localizador oficial de Magnus Medical en magnusmedical.com/find-a-provider. Si la clínica no figura ahí, no es un proveedor verificado de SAINT®.`,
        `2. Pregunte si el software de orientación está autorizado por la FDA para orientación personalizada por fMRI. SAINT® usa el Sistema de Neuromodulación Magnus autorizado por la FDA (510(k) K220177, septiembre de 2022); otros programas "guiados por fMRI" usan sus propios métodos, no autorizados por la FDA para este fin.`,
        `3. Pregunte si el protocolo exacto se probó en los ensayos clínicos de SAINT. El resultado de remisión del 78,6 % (Cole et al., Am J Psychiatry 2022;179:132–141) proviene únicamente de SAINT® y no se transfiere a un programa que no estuvo en el ensayo.`,
      ],
    },
    {
      heading: "Señales de alerta en los artículos de SAINT™",
      paragraphs: [
        `Algunos artículos usan el nombre y los números de los ensayos de SAINT para vender un programa de "TMS acelerada guiada por fMRI" de menor costo y uso off-label por unos $9.900. Esté atento a estas señales de que la página no describe SAINT®:`,
      ],
      list: [
        `Un titular que dice SAINT pero luego recomienda un programa "alternativo" o "similar".`,
        `Una página que cita el 79 % de remisión de SAINT (78,6 % en el ensayo de 2022) junto a un programa distinto.`,
        `Expresiones como "estilo SAINT", "inspirado en SAINT" o "como SAINT" en lugar del SAINT® autorizado por la FDA.`,
        `Un precio (unos $9.900) con extras como masaje, terapia de luz roja o fisioterapia incluidos.`,
        `Una clínica que no puede nombrar el Sistema de Neuromodulación Magnus ni mostrar su listado en el localizador de Magnus Medical.`,
      ],
    },
    {
      heading: "Por qué importa la verificación",
      paragraphs: [
        `La orientación guiada por fMRI de SAINT® es central en el protocolo. Una clínica que ofrece "TMS acelerada" sin el sistema Magnus ni orientación por fMRI no está ofreciendo SAINT®: ofrece un calendario acelerado off-label en un dispositivo distinto. Los resultados de los ensayos de SAINT aplican solo al sistema SAINT®. [[Vea la comparación completa lado a lado|/es/learn/saint-vs-accelerated-tms]] o [[consulte las ubicaciones de SAINT®|/es/learn/saint-tms-locations]].`,
      ],
    },
  ],
  faqs: [
    {
      question: `¿Cómo verifico un proveedor de SAINT®?`,
      answer: `Busque la clínica en el localizador oficial de Magnus Medical (magnusmedical.com/find-a-provider), pregunte si el software de orientación está autorizado por la FDA (510(k) K220177) y pregunte si el protocolo exacto se probó en los ensayos clínicos de SAINT. Una clínica que no puede confirmar las tres cosas no ofrece el protocolo SAINT® autorizado por la FDA.`,
    },
    {
      question: `¿Qué significa "proveedor verificado de SAINT®"?`,
      answer: `Un proveedor verificado de SAINT® es una clínica que figura en el localizador oficial de Magnus Medical como autorizada para ofrecer SAINT® en el Sistema de Neuromodulación Magnus, con capacitación certificada por el fabricante y la tecnología de orientación por fMRI requerida. Neuro Wellness TMS Centers of America en Coral Springs, Florida, es un proveedor verificado de SAINT®.`,
    },
    {
      question: `¿Puede una clínica que no está en el localizador ofrecer SAINT®?`,
      answer: `No. Una clínica que ofrece "TMS acelerada" o "TMS de 5 días" sin el Sistema de Neuromodulación Magnus ni orientación por fMRI ofrece un calendario off-label en un dispositivo distinto, no el protocolo SAINT® autorizado por la FDA. Consulte el localizador de Magnus Medical y haga las preguntas anteriores antes de pagar.`,
    },
  ],
  sources: [
    {
      citation:
        "Magnus Medical — localizador de proveedores de SAINT® (magnusmedical.com/find-a-provider).",
      url: "https://www.magnusmedical.com/find-a-provider",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System with SAINT Technology (K220177, septiembre de 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
  ],
  relatedLinks: [
    {
      title: `Aviso de SAINT\u00ae: los programas parecidos no son SAINT\u00ae`,
      href: "/es/learn/saint-disclaimer",
      description:
        "Nuestro aviso formal: Cognitive FX y otros programas \u201cguiados por fMRI\u201d no son SAINT\u00ae",
    },
    {
      title: `SAINT\u00ae frente a la TMS acelerada de $9,900: no es el mismo tratamiento`,
      href: "/es/learn/saint-vs-accelerated-tms",
      description:
        "Comparaci\u00f3n completa: autorizaci\u00f3n de la FDA, orientaci\u00f3n, evidencia, precio",
    },
    {
      title: "Ubicaciones de SAINT® TMS",
      href: "/es/learn/saint-tms-locations",
      description: "Proveedores verificados y cómo viajar a Florida",
    },
    {
      title: "Encuentre un proveedor verificado de SAINT®",
      href: "/es/find-a-verified-saint-provider",
      description: "Nuestro listado oficial en Magnus Medical",
    },
    {
      title: "¿Qué es SAINT® TMS?",
      href: "/es/learn/what-is-saint-tms",
      description:
        "El protocolo de 5 días guiado por fMRI autorizado por la FDA",
    },
    {
      title: "¿Quién califica para SAINT® TMS?",
      href: "/es/learn/who-qualifies-for-saint-tms",
      description: "Elegibilidad, listas y próximos pasos",
    },
    {
      title: "SAINT® TMS acelerada",
      href: "/es/saint",
      description: "El protocolo de 5 días guiado por fMRI que ofrecemos",
    },
  ],
  reviewBlock,
  toc: [
    {
      label: "Verificación de 3 pasos",
      href: "#una-verificacion-de-3-pasos-antes-de-pagar",
    },
    {
      label: "Señales de alerta",
      href: "#senales-de-alerta-en-los-articulos-de-saint",
    },
    { label: "Por qué importa", href: "#por-que-importa-la-verificacion" },
    { label: "Preguntas frecuentes", href: "#faq" },
    { label: "Investigación citada", href: "#cited-research" },
  ],
  safetyBlock:
    "SAINT® no es para todos. Está contraindicado para personas con metal sensible a campos magnéticos no extraíble cerca de la cabeza, ciertos dispositivos implantados o presión intracraneal aumentada. Los posibles efectos secundarios incluyen cefalea, fatiga, molestias en el cuero cabelludo y ansiedad; la TMS conlleva un pequeño riesgo de convulsión. Si está en crisis, llame o envíe un mensaje de texto al 988.",
  trademarkFooter:
    "SAINT® es una marca registrada de Stanford University, con licencia exclusiva a Magnus Medical, Inc. Neuro Wellness TMS Centers of America no está afiliada a Stanford University y no nombra ni respalda a ninguna otra clínica o empresa.",
  ctaBand: {
    headline: "Verifique antes de pagar — y empiece sin costo",
    primaryText: "Vea si puedo ser candidato",
    primaryHref: "/es/learn/who-qualifies-for-saint-tms",
    secondaryText: "Hable con una persona",
    secondaryHref: "tel:9548279793",
    phone: "954-827-9793",
  },
} satisfies EsArticleInput);

export const esWhereCanIGetSaintTreatment = makeEsArticle({
  slug: "where-can-i-get-saint-treatment-for-depression",
  title: "¿Dónde puedo recibir SAINT® para la depresión? | Neuro Wellness",
  description:
    "SAINT® se ofrece en centros especializados. Neuro Wellness TMS Centers of America lo ofrece en Coral Springs, FL, para pacientes locales y de fuera del estado.",
  h1: "¿Dónde puedo recibir SAINT® para la depresión?",
  breadcrumbLabel: "Dónde recibir SAINT®",
  shortAnswer:
    "SAINT® se ofrece en centros especializados. Neuro Wellness TMS Centers of America lo ofrece en Coral Springs, FL. Atendemos a pacientes locales y de fuera del estado; el protocolo de cinco días hace que viajar sea práctico. [[Vea nuestra página de SAINT®|/es/saint]] o llame al 954-827-9793.",
  keyTakeaways: [
    "SAINT® se ofrece en centros especializados.",
    "Neuro Wellness lo ofrece en Coral Springs, FL.",
    "Atendemos a pacientes locales y de fuera del estado.",
    "El protocolo de cinco días hace que viajar sea práctico.",
  ],
  sections: [
    {
      heading: "Dónde recibir SAINT®",
      paragraphs: [
        "SAINT® se ofrece en centros especializados. Neuro Wellness TMS Centers of America lo ofrece en Coral Springs, FL. Atendemos a pacientes locales y de fuera del estado.",
      ],
    },
    {
      heading: "Viajar para recibir SAINT®",
      paragraphs: [
        "El protocolo de cinco días hace que viajar sea práctico. [[Vea información sobre viajar para SAINT®|/es/saint-tms-travel]] o llame al 954-827-9793.",
      ],
    },
  ],
  faqs,
  sources: [],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);
